import {
  getOrganizationTreeRowEstimate,
  getVisibleProjectPaginationCandidate,
} from '@/features/organization-project-picker/lib/organizationTree';
import type {
  OrganizationTreeDensity,
  OrganizationTreeRow,
} from '@/features/organization-project-picker/types/organizationTree';
import { useVirtualScrollMargin } from '@/shared/hooks/useVirtualScrollMargin';
import { shouldFetchNextVirtualPage } from '@/shared/lib/infiniteQuery';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useRef } from 'react';

interface OrganizationTreePaginationQuery {
  isPlaceholderData: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  fetchNextPage: () => unknown;
}

/** Owns virtual measurement and scroll-driven organization/project pagination for a tree model. */
export function useOrganizationTreeVirtualizer({
  rows,
  query,
  density,
  fetchNextProjectPage,
}: {
  rows: readonly OrganizationTreeRow[];
  query: OrganizationTreePaginationQuery;
  density: OrganizationTreeDensity;
  fetchNextProjectPage: (
    candidate: ReturnType<typeof getVisibleProjectPaginationCandidate>,
    isScrolling: boolean,
  ) => void;
}) {
  const scrollRef = useRef<HTMLElement>(null);
  const treeListRef = useRef<HTMLUListElement>(null);
  const scrollMargin = useVirtualScrollMargin(scrollRef, treeListRef, rows.length);
  const getTreeRowKey = useCallback((index: number) => rows[index]?.key ?? index, [rows]);
  const virtualizer = useVirtualizer<HTMLElement, HTMLLIElement>({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    getItemKey: getTreeRowKey,
    estimateSize: (index) => getOrganizationTreeRowEstimate(rows[index], density),
    initialRect: { width: 280, height: 640 },
    onChange: (instance, isScrolling) => {
      if (
        shouldFetchNextVirtualPage(
          instance,
          isScrolling,
          rows.length,
          query.isPlaceholderData ? false : query.hasNextPage,
          query.isFetchingNextPage,
          query.isFetchNextPageError,
        )
      ) {
        void query.fetchNextPage();
      }

      fetchNextProjectPage(
        getVisibleProjectPaginationCandidate(rows, instance.getVirtualItems()),
        isScrolling,
      );
    },
    overscan: 6,
    scrollMargin,
  });

  return { scrollRef, treeListRef, scrollMargin, virtualizer };
}
